import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  MapPin, Wrench, User, Phone, Mail, Calendar, Clock, FileText,
  ChevronLeft, ChevronRight, RotateCcw, CalendarDays, Search
} from "lucide-react";

const SERVICE_TYPES = ["Battery Check", "Motor Service", "Charger Services", "Diagnostic Check", "General Maintenance", "Brake Inspection"];
const LOCATIONS = [
  { value: "loc1", label: "Location 1", address: "123, EV Street, Green City, 560001" },
  { value: "loc2", label: "Location 2", address: "456, Motor Ave, Tech Park, 560002" },
];

const TIME_SLOTS = [
  "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM", "01:00 PM", "01:30 PM", "02:00 PM", "02:30 PM",
  "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM", "05:00 PM", "05:30 PM",
];

const parseTo24h = (slot) => {
  const [time, period] = slot.split(' ');
  const [h, m] = time.split(':').map(Number);
  let hour24 = h;
  if (period === 'PM' && h !== 12) hour24 += 12;
  if (period === 'AM' && h === 12) hour24 = 0;
  return { hour: hour24, min: m };
};

const isSlotPast = (slot, selectedDate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dateObj = new Date(selectedDate + 'T00:00:00');
  if (dateObj.getTime() !== today.getTime()) return false;
  const now = new Date();
  const { hour, min } = parseTo24h(slot);
  return hour < now.getHours() || (hour === now.getHours() && min <= now.getMinutes());
};

const formatSlotDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
};

const BookingPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    location: "loc1", service_type: "", customer_name: "", mobile_number: "",
    email: "", notes: "", booking_source: "Website",
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    const day = d.getDay(); const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  });
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [slotStatuses, setSlotStatuses] = useState({});
  const [slotsLoading, setSlotsLoading] = useState(false);
  const fetchIdRef = useRef(0);

  // Fetch real slot availability when date changes — with race condition guard + loading state
  useEffect(() => {
    if (!selectedDate) return;
    const currentFetchId = ++fetchIdRef.current;
    setSlotsLoading(true);
    setSlotStatuses({}); // Clear stale slots immediately
    const fetchSlots = async () => {
      try {
        const res = await axios.get(`${API}/bookings/slots?date=${selectedDate}`);
        if (currentFetchId === fetchIdRef.current) {
          setSlotStatuses(res.data);
        }
      } catch {
        if (currentFetchId === fetchIdRef.current) {
          const fallback = {};
          TIME_SLOTS.forEach(slot => { fallback[slot] = { count: 0, status: 'available' }; });
          setSlotStatuses(fallback);
        }
      } finally {
        if (currentFetchId === fetchIdRef.current) {
          setSlotsLoading(false);
        }
      }
    };
    fetchSlots();
  }, [selectedDate]);

  const today = useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  }, []);

  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      const t = new Date(); t.setHours(0, 0, 0, 0);
      days.push({
        date: d, dayName: d.toLocaleDateString("en-US", { weekday: "short" }),
        dateLabel: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        dateStr: d.toISOString().split("T")[0],
        isToday: d.getTime() === t.getTime(),
        isPast: d < t,
      });
    }
    return days;
  }, [weekStart]);

  useEffect(() => {
    if (!selectedDate) {
      setSelectedDate(today.toISOString().split("T")[0]);
    }
  }, []);

  const searchParties = useCallback(async (query) => {
    if (!query || query.length < 3) { setSuggestions([]); setShowSuggestions(false); return; }
    try {
      const res = await axios.get(`${API}/parties/search-mobile?q=${encodeURIComponent(query)}`);
      setSuggestions(res.data); setShowSuggestions(res.data.length > 0);
    } catch { setSuggestions([]); }
  }, []);

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const toggleSlot = (slot) => {
    setSelectedSlots(prev =>
      prev.includes(slot) ? prev.filter(s => s !== slot) : [...prev, slot]
    );
  };

  const handleCreateBooking = async () => {
    if (!formData.customer_name.trim()) { toast.error("Customer name is required"); return; }
    if (!formData.mobile_number.trim()) { toast.error("Mobile number is required"); return; }
    if (!formData.service_type) { toast.error("Please select a service type"); return; }
    if (!selectedDate) { toast.error("Please select a date"); return; }
    if (selectedSlots.length === 0) { toast.error("Please select at least one time slot"); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, {
        customer_name: formData.customer_name,
        mobile_number: formData.mobile_number,
        email: formData.email,
        service_type: formData.service_type,
        location: formData.location,
        date: selectedDate,
        time_slots: selectedSlots,
        notes: formData.notes,
        booking_source: formData.booking_source,
      });
      toast.success("Booking created successfully!");
      navigate("/bookings");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally { setLoading(false); }
  };

  const handleReset = () => {
    setFormData({ location: "loc1", service_type: "", customer_name: "", mobile_number: "", email: "", notes: "", booking_source: "Website" });
    setSelectedSlots([]);
  };

  const selectedLocation = LOCATIONS.find(l => l.value === formData.location);
  const weekLabel = `${weekDays[0]?.date.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${weekDays[6]?.date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;

  const sortedSelectedSlots = [...selectedSlots].sort((a, b) => {
    const pa = parseTo24h(a), pb = parseTo24h(b);
    return pa.hour * 60 + pa.min - (pb.hour * 60 + pb.min);
  });

  const totalDuration = selectedSlots.length * 30;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-[22px] font-bold text-[#1E293B]">New Booking</h1>
        <p className="text-[13px] text-[#94A3B8]">Bookings &gt; New Booking</p>
      </div>

      <div className="flex gap-6">
        <div className="flex-1 min-w-0 space-y-6">
          {/* Step 1: Booking Information */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#F1F5F9]">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-7 h-7 rounded-full bg-[#3B82F6] flex items-center justify-center text-white text-[13px] font-bold">1</div>
              <h2 className="text-[16px] font-bold text-[#1E293B]">Booking Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="text-[12px] text-[#475569] mb-1.5 block">Location <span className="text-red-500">*</span></Label>
                <Select value={formData.location} onValueChange={(v) => set("location", v)}>
                  <SelectTrigger data-testid="booking-location" className="h-12 bg-[#F8FAFC] border-[#E2E8F0] text-[13px]">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#64748B]" />
                      <div className="text-left">
                        <p className="font-medium text-[#1E293B]">{selectedLocation?.label}</p>
                        <p className="text-[10px] text-[#94A3B8]">{selectedLocation?.address}</p>
                      </div>
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATIONS.map(l => <SelectItem key={l.value} value={l.value}>{l.label} - {l.address}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px] text-[#475569] mb-1.5 block">Service Type <span className="text-red-500">*</span></Label>
                <Select value={formData.service_type} onValueChange={(v) => set("service_type", v)}>
                  <SelectTrigger data-testid="booking-service" className="h-12 bg-[#F8FAFC] border-[#E2E8F0] text-[13px]">
                    <div className="flex items-center gap-2">
                      <Wrench className="w-4 h-4 text-[#64748B]" />
                      <SelectValue placeholder="Select service type" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <Label className="text-[12px] text-[#475569] mb-1.5 block">Customer Name <span className="text-red-500">*</span></Label>
                <Input data-testid="booking-name" value={formData.customer_name}
                  onChange={(e) => set("customer_name", e.target.value)}
                  placeholder="Customer name" className="h-10 bg-[#F8FAFC] border-[#E2E8F0] text-[13px]" />
              </div>
              <div className="relative">
                <Label className="text-[12px] text-[#475569] mb-1.5 block">Mobile Number <span className="text-red-500">*</span></Label>
                <div className="flex gap-1">
                  <div className="h-10 px-3 bg-[#F1F5F9] border border-[#E2E8F0] rounded-md flex items-center text-[13px] text-[#64748B] shrink-0">+91</div>
                  <Input data-testid="booking-mobile" value={formData.mobile_number}
                    onChange={(e) => { set("mobile_number", e.target.value); searchParties(e.target.value); }}
                    onFocus={() => { if (suggestions.length) setShowSuggestions(true); }}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    placeholder="Mobile number" className="h-10 bg-[#F8FAFC] border-[#E2E8F0] text-[13px] flex-1" />
                </div>
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute z-50 top-[68px] left-0 right-0 bg-white border border-[#E2E8F0] rounded-lg shadow-lg max-h-40 overflow-y-auto" data-testid="booking-suggestions">
                    <div className="p-2 text-[10px] text-[#94A3B8] border-b">Existing customers:</div>
                    {suggestions.map(p => (
                      <button key={p.id} type="button"
                        onClick={() => { set("customer_name", p.party_name); set("mobile_number", p.mobile_number); set("email", p.email || ""); setShowSuggestions(false); }}
                        className="w-full text-left px-3 py-2 hover:bg-[#EFF6FF] text-[12px] flex justify-between">
                        <span className="font-medium text-[#1E293B]">{p.party_name}</span>
                        <span className="text-[#94A3B8]">{p.mobile_number}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-[12px] text-[#475569] mb-1.5 block">Email (Optional)</Label>
                <Input data-testid="booking-email" type="email" value={formData.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="email@example.com" className="h-10 bg-[#F8FAFC] border-[#E2E8F0] text-[13px]" />
              </div>
            </div>

            <div className="mb-4">
              <Label className="text-[12px] text-[#475569] mb-1.5 block">Notes (Optional)</Label>
              <Textarea data-testid="booking-notes" value={formData.notes}
                onChange={(e) => set("notes", e.target.value.slice(0, 250))}
                placeholder="Customer issue description, special instructions..."
                className="bg-[#F8FAFC] border-[#E2E8F0] text-[13px] min-h-[80px]" />
              <p className="text-[10px] text-[#CBD5E1] mt-1">Max 250 characters</p>
            </div>

            <div className="w-48">
              <Label className="text-[12px] text-[#475569] mb-1.5 block">Booking Source</Label>
              <Select value={formData.booking_source} onValueChange={(v) => set("booking_source", v)}>
                <SelectTrigger data-testid="booking-source" className="h-9 bg-[#F8FAFC] border-[#E2E8F0] text-[12px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Website">Website</SelectItem>
                  <SelectItem value="Walk-In">Walk-In</SelectItem>
                  <SelectItem value="Phone Call">Phone Call</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Step 2: Select Date & Time */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#F1F5F9]">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-7 h-7 rounded-full bg-[#3B82F6] flex items-center justify-center text-white text-[13px] font-bold">2</div>
              <h2 className="text-[16px] font-bold text-[#1E293B]">Select Date & Time</h2>
            </div>

            {/* Week nav */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#64748B]" />
                <span className="text-[13px] font-semibold text-[#1E293B]">{weekLabel}</span>
                <button data-testid="week-prev" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }}
                  className="p-1 rounded hover:bg-[#F1F5F9]"><ChevronLeft className="w-4 h-4 text-[#64748B]" /></button>
                <button data-testid="week-next" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }}
                  className="p-1 rounded hover:bg-[#F1F5F9]"><ChevronRight className="w-4 h-4 text-[#64748B]" /></button>
              </div>
              <div className="flex items-center gap-4 text-[11px]">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#4CAF50]" /> Available</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#FF9800]" /> Busy</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#EF4444]" /> Full</span>
              </div>
            </div>

            {/* Day cards */}
            <div className="grid grid-cols-7 gap-2 mb-5">
              {weekDays.map((d) => {
                const isSelected = selectedDate === d.dateStr;
                return (
                  <button key={d.dateStr} data-testid={`day-${d.dateStr}`}
                    onClick={() => { if (!d.isPast) { setSelectedDate(d.dateStr); setSelectedSlots([]); } }}
                    disabled={d.isPast}
                    className={`rounded-xl p-3 text-center transition-all ${
                      d.isPast
                        ? "bg-[#F1F5F9] border border-[#E2E8F0] opacity-40 cursor-not-allowed"
                        : isSelected ? "bg-[#3B82F6] text-white shadow-md" : "border border-[#E2E8F0] hover:border-[#3B82F6]"
                    }`}>
                    <p className={`text-[12px] font-semibold ${d.isPast ? "text-[#CBD5E1]" : isSelected ? "text-blue-100" : "text-[#94A3B8]"}`}>{d.dayName}</p>
                    <p className={`text-[14px] font-bold mt-0.5 ${d.isPast ? "text-[#CBD5E1]" : isSelected ? "text-white" : "text-[#1E293B]"}`}>{d.dateLabel}</p>
                    {d.isToday && <div className={`w-1.5 h-1.5 rounded-full mx-auto mt-1 ${isSelected ? "bg-white" : "bg-[#3B82F6]"}`} />}
                  </button>
                );
              })}
            </div>

            {/* Time slots — synced to selectedDate, with loading skeleton */}
            {selectedDate && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[13px] font-semibold text-[#1E293B]" data-testid="slot-header-date">
                    Available Slots for {formatSlotDate(selectedDate)}
                  </p>
                  <p className="text-[11px] text-[#94A3B8]">Select multiple 30-min slots</p>
                </div>

                {slotsLoading ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mb-4" data-testid="slots-skeleton">
                    {TIME_SLOTS.map((slot) => (
                      <div key={slot} className="rounded-xl py-2.5 px-1 bg-[#F8FAFC] border border-[#F1F5F9] animate-pulse">
                        <div className="h-4 bg-[#E2E8F0] rounded mx-3 mb-1" />
                        <div className="h-2.5 bg-[#F1F5F9] rounded mx-5" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mb-4" data-testid="slots-grid">
                    {TIME_SLOTS.map((slot) => {
                      const slotInfo = slotStatuses[slot] || { count: 0, status: 'available' };
                      const status = slotInfo.status;
                      const isSelected = selectedSlots.includes(slot);
                      const isFull = status === 'full';
                      const isPast = isSlotPast(slot, selectedDate);
                      const isDisabled = isFull || isPast;

                      return (
                        <button key={slot} data-testid={`slot-${slot.replace(/\s/g, '-')}`}
                          onClick={() => !isDisabled && toggleSlot(slot)}
                          disabled={isDisabled}
                          className={`rounded-xl py-2.5 px-1 text-center transition-all border ${
                            isSelected
                              ? "bg-[#3B82F6] text-white border-[#3B82F6] shadow-md ring-2 ring-blue-200"
                              : isPast
                              ? "bg-[#F1F5F9] border-[#E2E8F0] text-[#94A3B8] cursor-not-allowed"
                              : isFull
                              ? "bg-[#FEF2F2] border-[#FECACA] text-[#EF4444] cursor-not-allowed"
                              : status === "busy"
                              ? "bg-[#FFFBEB] border-[#FED7AA] hover:border-[#FF9800]"
                              : "bg-[#F0FDF4] border-[#BBF7D0] hover:border-[#4CAF50]"
                          }`}>
                          <p className={`text-[12px] font-semibold ${isSelected ? "text-white" : isDisabled ? (isPast ? "text-[#CBD5E1]" : "text-[#EF4444]") : "text-[#1E293B]"}`}>{slot}</p>
                          <p className={`text-[9px] mt-0.5 font-medium ${
                            isSelected ? "text-blue-200"
                            : isPast ? "text-[#CBD5E1]"
                            : isFull ? "text-[#EF4444]"
                            : status === "busy" ? "text-[#F59E0B]"
                            : "text-[#4CAF50]"
                          }`}>
                            {isSelected ? "Selected" : isPast ? "Past" : isFull ? "Full" : status === "busy" ? `Busy (${slotInfo.count}/3)` : "Available"}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                )}

                {selectedSlots.length > 0 && (
                  <div className="bg-[#EFF6FF] rounded-xl p-3 flex items-center justify-between text-[12px]">
                    <p className="text-[#1E293B]">
                      <Clock className="w-3.5 h-3.5 inline mr-1 text-[#3B82F6]" />
                      {selectedSlots.length} slot{selectedSlots.length > 1 ? "s" : ""} selected: {sortedSelectedSlots.join(", ")}
                    </p>
                    <span className="text-[#3B82F6] font-semibold">Total: {totalDuration} mins</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right sidebar */}
        <div className="hidden lg:block w-[280px] shrink-0 space-y-5">
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#F1F5F9] sticky top-6">
            <div className="flex items-center gap-2 mb-4">
              <CalendarDays className="w-4 h-4 text-[#3B82F6]" />
              <h3 className="text-[15px] font-bold text-[#1E293B]">Booking Summary</h3>
            </div>
            <div className="space-y-3 text-[12px]">
              {[
                { icon: MapPin, label: "Location", value: selectedLocation?.label || "-" },
                { icon: Wrench, label: "Service Type", value: formData.service_type || "-" },
                { icon: User, label: "Customer Name", value: formData.customer_name || "-" },
                { icon: Phone, label: "Mobile Number", value: formData.mobile_number ? `+91 ${formData.mobile_number}` : "-" },
                { icon: Mail, label: "Email", value: formData.email || "-" },
                { icon: Calendar, label: "Date", value: formatSlotDate(selectedDate) || "-" },
                { icon: Clock, label: "Time Slots", value: selectedSlots.length > 0 ? `${sortedSelectedSlots.join(", ")} (${totalDuration} min)` : "-" },
                { icon: FileText, label: "Notes", value: formData.notes || "-" },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className="flex items-start gap-2.5">
                    <Icon className="w-3.5 h-3.5 text-[#94A3B8] mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[#94A3B8]">{item.label}</p>
                      <p className="text-[#1E293B] font-medium truncate">{item.value}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-5 space-y-2">
              <Button data-testid="create-booking-btn" onClick={handleCreateBooking} disabled={loading}
                className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white h-10 text-[13px] font-semibold rounded-lg gap-1.5">
                <CalendarDays className="w-4 h-4" />
                {loading ? "Creating..." : "Create Booking"}
              </Button>
              <Button data-testid="reset-booking-btn" onClick={handleReset} variant="outline"
                className="w-full h-9 text-[12px] gap-1.5 border-[#E2E8F0] text-[#64748B]">
                <RotateCcw className="w-3.5 h-3.5" /> Reset
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
